import { Trans } from '@lingui/react/macro';

import type { ImageAnalyses } from '@/@types/latestImagesV2';
import { PerioChart } from '@/common-components/composed/PerioChart/PerioChart';
import type { GetClaimSummaryResponse } from '@/modules/claims/freeClaims/insuranceExport/types';

import type {
  DateFilterState,
  SelectionModalActions,
  SelectionModalSelectors,
} from '../../../hooks/useImageSelectionModalState';

import { ImageGridSubsection } from './ImageGridSubsection';
import { SelectableImageCard } from './SelectableImageCard';

type OtherSectionProps = {
  dateStr: string;
  filters: DateFilterState;
  selectors: SelectionModalSelectors;
  claimSummary: GetClaimSummaryResponse | undefined;
  filteredRadiographs: ImageAnalyses[];
  filteredColorImages: ImageAnalyses[];
  selectedTreatmentIds: string[];
  actions: SelectionModalActions;
  viewFilterImageTypes: Set<string>;
};

export const OtherSection = ({
  dateStr,
  filters,
  selectors,
  claimSummary,
  filteredRadiographs,
  filteredColorImages,
  selectedTreatmentIds,
  actions,
  viewFilterImageTypes,
}: OtherSectionProps) => {
  const showColorImages = selectors
    .presentTypesByDate(dateStr)
    .includes('colorImages');
  const showPeriochart = selectors
    .presentTypesByDate(dateStr)
    .includes('periochart');
  const hasColorImages =
    showColorImages &&
    viewFilterImageTypes.has('colorImages') &&
    Object.keys(filters.colorImages ?? {}).length > 0;
  const hasPeriochart =
    showPeriochart &&
    viewFilterImageTypes.has('periochart') &&
    filters['periochart-present'] &&
    !!claimSummary?.perioChart;
  if (!hasColorImages && !hasPeriochart) return null;

  return (
    <div className="vai-flex vai-flex-col vai-gap-2">
      <h3 className="vai-font-[proxima-nova] vai-text-sm vai-font-semibold vai-text-zinc-600 vai-mb-1">
        <Trans>OTHER</Trans>
      </h3>
      {hasColorImages && (
        <ImageGridSubsection
          title={<Trans>Intraoral Photos</Trans>}
          entries={filters.colorImages ?? {}}
          claimSummary={claimSummary}
          filteredRadiographs={filteredRadiographs}
          filteredColorImages={filteredColorImages}
          selectedTreatmentIds={selectedTreatmentIds}
          onToggle={(imageID) => actions.toggleColorImageId(dateStr, imageID)}
        />
      )}

      {hasPeriochart && (
        <div className="vai-flex vai-flex-col vai-gap-2">
          <h3 className="vai-text-sm vai-font-semibold vai-text-foreground">
            <Trans>Perio Chart</Trans>
          </h3>
          <div className="vai-grid md:vai-grid-cols-3 vai-gap-3">
            <SelectableImageCard
              checked={selectors.periochartState(dateStr).selected}
              onChange={() => actions.togglePeriochart(dateStr)}
            >
              <div className="vai-h-40 vai-w-full vai-bg-gray-100">
                <PerioChart
                  mode="light"
                  perioChartData={claimSummary!.perioChart!}
                />
              </div>
            </SelectableImageCard>
          </div>
        </div>
      )}
    </div>
  );
};
